"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.withFocusable = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var React = _interopRequireWildcard(require("react"));
var _hoistNonReactMethods = _interopRequireDefault(require("hoist-non-react-methods"));
var _getDisplayName = require("../utils/getDisplayName");
var _FocusableSt = require("./Focusable.st.css");
var _isStatelessComponent = require("../utils/isStatelessComponent");
var _jsxFileName = "/home/builduser/work/31dad9578a4e06bb/packages/wix-ui-tpa/dist/cjs/components/internal/WixUiCoreComponents/components/CoreButtonNext/FocusableHoc/FocusableHOC.tsx";
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
/**
 * Assigned the given propTypes to the given class.
 *
 * This is a hack because since Yoshi3, with babel-preset-yoshi,
 * the babel-plugin-transform-react-remove-prop-types is enabled and removes propTypes.
 *
 * So if we simply do FocusableHOC.propTypes = Component.propTypes, it is being stripped away.
 *
 * This later becomes a problem if another component defines:
 * <code>
 * Comp.propTypes = {
 *   prop1: SomeFocusableComp.propTypes.prop1
 * }
 * </code>
 */
const assignPropTypesHack = (targetClass, propTypes) => {
  targetClass.propTypes = propTypes;
};

/**
 * Singleton for managing current input method (keyboard or mouse).
 */
const inputMethod = new class {
  // Default is keyboard in case an element is focused programmatically.

  constructor() {
    (0, _defineProperty2.default)(this, "method", 'keyboard');
    (0, _defineProperty2.default)(this, "subscribers", new Map());
    (0, _defineProperty2.default)(this, "subscribe", (target, callback) => this.subscribers.set(target, callback));
    (0, _defineProperty2.default)(this, "unsubscribe", target => this.subscribers.delete(target));
    /**
     * Is the current input method `keyboard`. if `false` is means it is `mouse`
     */
    (0, _defineProperty2.default)(this, "isKeyboard", () => this.method === 'keyboard');
    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', () => this.setMethod('mouse'));
      window.addEventListener('keydown', () => this.setMethod('keyboard'));
      // We need to listen on keyUp, in case a TAB is made from the browser's address-bar,
      // so the keyDown is not fired, only the keyUp.
      window.addEventListener('keyup', () => this.setMethod('keyboard'));
    }
  }
  setMethod(method) {
    if (method !== this.method) {
      this.method = method;
      this.subscribers.forEach(f => f());
    }
  }
}();

/*
 * TODO: Consider adding 'disabled' state to this HOC, since:
 * - When component is focused and then it becomes disabled, then the focus needs to be blured.
 *
 * TODO: Consider using [Recompose](https://github.com/acdlite/recompose/tree/master/src/packages/recompose) to do:
 *  - the static hoisting
 *  - set displayName
 */
const withFocusable = Component => {
  class FocusableHOC extends React.Component {
    constructor(...args) {
      super(...args);
      (0, _defineProperty2.default)(this, "focusedByMouse", false);
      (0, _defineProperty2.default)(this, "wrappedComponentRef", null);
      (0, _defineProperty2.default)(this, "state", {
        focus: false,
        focusVisible: false
      });
      (0, _defineProperty2.default)(this, "focus", () => {
        if (this.wrappedComponentRef && this.wrappedComponentRef.focus) {
          this.wrappedComponentRef.focus();
        }
      });
      (0, _defineProperty2.default)(this, "markAsFocused", () => {
        this.setState({
          focus: true,
          focusVisible: inputMethod.isKeyboard()
        });
        inputMethod.subscribe(this, () => {
          if (inputMethod.isKeyboard()) {
            this.setState({
              focusVisible: true
            });
          }
        });
      });
      (0, _defineProperty2.default)(this, "markAsBlurred", () => {
        inputMethod.unsubscribe(this);
        this.setState({
          focus: false,
          focusVisible: false
        });
      });
      (0, _defineProperty2.default)(this, "onFocus", event => {
        const {
          onFocus
        } = this.props;
        onFocus ? onFocus(event, {
          blur: this.markAsBlurred,
          focus: this.markAsFocused
        }) : this.markAsFocused();
      });
      (0, _defineProperty2.default)(this, "onBlur", event => {
        const {
          onBlur
        } = this.props;
        onBlur ? onBlur(event, {
          blur: this.markAsBlurred,
          focus: this.markAsFocused
        }) : this.markAsBlurred();
      });
    }
    componentWillUnmount() {
      inputMethod.unsubscribe(this);
    }
    componentDidUpdate(prevProps) {
      /*
        in case when button was focused and then become disabled,
        we need to trigger blur logic and remove all listers, as disabled button
        do not trigger onFocus and onBlur events
      */
      const isFocused = this.state.focus || this.state.focusVisible;
      const isBecomeDisabled = !prevProps.disabled && this.props.disabled;
      if (isFocused && isBecomeDisabled) {
        this.onBlur({});
      }
    }
    render() {
      const reference = (0, _isStatelessComponent.isStatelessComponent)(Component) ? undefined : ref => this.wrappedComponentRef = ref;
      return /*#__PURE__*/React.createElement(Component, (0, _extends2.default)({}, this.props, {
        ref: reference,
        focusableOnFocus: this.onFocus,
        focusableOnBlur: this.onBlur,
        className: (0, _FocusableSt.st)(_FocusableSt.classes.root, {
          focus: this.state.focus,
          'focus-visible': this.state.focusVisible
        }, this.props.className),
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 151,
          columnNumber: 9
        }
      }));
    }
  }
  (0, _defineProperty2.default)(FocusableHOC, "displayName", (0, _getDisplayName.getDisplayName)(Component));
  (0, _defineProperty2.default)(FocusableHOC, "defaultProps", Component.defaultProps);
  assignPropTypesHack(FocusableHOC, Component.propTypes);
  return (0, _isStatelessComponent.isStatelessComponent)(Component) ? FocusableHOC : (0, _hoistNonReactMethods.default)(FocusableHOC, Component, {
    delegateTo: c => c.wrappedComponentRef,
    hoistStatics: true
  });
};
exports.withFocusable = withFocusable;
//# sourceMappingURL=FocusableHOC.js.map